import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import SEOHead from '@/components/feature/SEOHead';

function PageHero() {
  return (
    <section className="relative min-h-[50vh] flex items-end overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#1E3A8A] via-[#1E2A6E] to-[#2D1B69]" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-yellow/8 rounded-full blur-3xl pointer-events-none" />
      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 pb-20 pt-36">
        <span className="hero-badge inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6">
          We&apos;re Here To Help
        </span>
        <h1 className="hero-line-1 font-heading text-white text-6xl lg:text-7xl font-black mb-4">Contact Us</h1>
        <p className="hero-sub font-body text-white/75 text-xl max-w-2xl">
          We come to you — no commute, no office visit needed. Reach out to schedule an in-home or on-location session anywhere across North Carolina.
        </p>
      </div>
    </section>
  );
}

function ContactSection() {
  const { ref: leftRef, isVisible: leftVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: rightRef, isVisible: rightVis } = useScrollAnimation({ threshold: 0.2 });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;
    setStatus('submitting');
    try {
      const formData = new URLSearchParams();
      const elements = form.elements;
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        if (el.name && el.value && el.type !== 'submit') formData.append(el.name, el.value);
      }
      const res = await fetch('https://readdy.ai/api/form/d73f22suf8iffoai1qig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (res.ok) { setStatus('success'); form.reset(); setCharCount(0); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  const contactInfo = [
    { icon: 'ri-mail-line', label: 'Email', value: 'info@creativeinstitutellc.com', href: 'mailto:info@creativeinstitutellc.com' },
    { icon: 'ri-phone-line', label: 'Phone', value: '910-242-4567', href: 'tel:9102424567' },
    { icon: 'ri-map-pin-line', label: 'Service Area', value: 'Multiple Cities Across North Carolina · Mobile-Based', href: undefined },
    { icon: 'ri-time-line', label: 'Hours', value: 'Mon–Sat: 9am – 9pm', href: undefined },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className="flex flex-col lg:flex-row gap-16">
          <div ref={leftRef} className={`scroll-hidden-left ${leftVis ? 'scroll-visible' : ''} flex-shrink-0 w-full lg:w-[380px]`}>
            <h2 className="font-heading text-4xl font-black text-brand-blue-dark mb-4">Get In Touch</h2>
            <p className="font-body text-gray-500 text-base leading-relaxed mb-10">
              Whether you&apos;re ready to enroll, want to learn more about our mobile programs, or just have a question — reach out. We come to you, and we respond to every message.
            </p>
            <div className="flex flex-col gap-6">
              {contactInfo.map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-brand-blue-dark text-brand-yellow flex-shrink-0">
                    <i className={`${item.icon} text-lg`} />
                  </div>
                  <div>
                    <p className="font-body text-xs text-gray-400 uppercase tracking-widest mb-0.5">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="font-body text-brand-blue-dark font-semibold text-sm hover:text-brand-purple transition-colors cursor-pointer">{item.value}</a>
                    ) : (
                      <p className="font-body text-brand-blue-dark font-semibold text-sm">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7]/60 border border-brand-yellow/20">
              <p className="font-heading text-brand-blue-dark text-lg font-bold italic mb-2">&ldquo;Where Every Creative Mind Matters&rdquo;</p>
              <p className="font-body text-[#D97706] text-sm font-semibold">— Creative Institute</p>
            </div>

            <div className="mt-6 p-5 rounded-2xl bg-brand-blue-dark/5 border border-brand-blue-dark/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-yellow/20 flex-shrink-0">
                  <i className="ri-home-heart-line text-[#D97706] text-sm" />
                </div>
                <p className="font-body text-brand-blue-dark text-sm font-bold">100% Mobile &amp; In-Home</p>
              </div>
              <p className="font-body text-gray-500 text-xs leading-relaxed">
                We travel to your home, school, church, or community space — across Fayetteville, Hope Mills, Lumberton, and 20+ cities in NC.
              </p>
            </div>
          </div>

          <div ref={rightRef} className={`scroll-hidden-right ${rightVis ? 'scroll-visible' : ''} flex-1`}>
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center gap-5 p-10 rounded-2xl bg-gradient-to-br from-[#EEF2FF] to-[#F5F3FF]">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-brand-purple/15 text-brand-purple">
                  <i className="ri-check-double-line text-3xl" />
                </div>
                <h3 className="font-heading text-3xl font-black text-brand-blue-dark">Message Sent!</h3>
                <p className="font-body text-gray-500 max-w-sm">Thank you for reaching out. We&apos;ll be in touch with you shortly.</p>
                <button onClick={() => setStatus('idle')} className="whitespace-nowrap px-6 py-3 rounded-full bg-brand-purple text-white font-body font-semibold text-sm cursor-pointer hover:opacity-90 transition-opacity">Send Another</button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2 block">Full Name *</label>
                    <input name="name" required type="text" placeholder="Your name" className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple bg-gray-50 transition-colors" />
                  </div>
                  <div>
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2 block">Email *</label>
                    <input name="email" required type="email" placeholder="your@email.com" className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple bg-gray-50 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2 block">Phone</label>
                  <input name="phone" type="tel" placeholder="(910) 000-0000" className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple bg-gray-50 transition-colors" />
                </div>
                <div>
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2 block">Topic</label>
                  <select name="topic" className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple bg-gray-50 transition-colors">
                    <option value="">Select a topic</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Enrollment">Enrollment</option>
                    <option value="In-Home Programs">In-Home Programs</option>
                    <option value="Mobile / Off-Site Services">Mobile / Off-Site Services</option>
                    <option value="Recording Studio">Recording Studio</option>
                    <option value="Neurodivergent Development">Neurodivergent Development</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Service Area Question">Service Area Question</option>
                  </select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest">Message *</label>
                    <span className={`font-body text-xs ${charCount > 500 ? 'text-red-500' : 'text-gray-400'}`}>{charCount}/500</span>
                  </div>
                  <textarea name="message" required rows={5} maxLength={500} onChange={(e) => setCharCount(e.target.value.length)} placeholder="Tell us how we can help..." className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple bg-gray-50 transition-colors resize-none" />
                </div>
                {status === 'error' && <p className="font-body text-red-500 text-sm">Something went wrong. Please try again.</p>}
                <button type="submit" disabled={status === 'submitting' || charCount > 500} className="whitespace-nowrap w-full px-8 py-4 rounded-xl bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-bold text-base hover:opacity-90 transition-all cursor-pointer disabled:opacity-60">
                  {status === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function MapSection() {
  const cities = ['Fayetteville', 'Hope Mills', 'Spring Lake', 'Lumberton', 'Raeford', 'Rockingham', 'Laurinburg', 'Red Springs', 'Pembroke', 'Maxton', 'Rowland', 'Fairmont', 'Elizabethtown', 'Whiteville', 'Bladenboro', 'Clarkton', 'Tabor City', 'Loris', 'Dillon', 'Bennettsville', 'Hamlet', 'Laurel Hill'];

  return (
    <section className="bg-white pb-20">
      <div className="max-w-screen-xl mx-auto px-8">
        {/* Main banner */}
        <div className="rounded-3xl bg-brand-blue-dark p-10 flex flex-col md:flex-row items-center gap-8 mb-8">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-yellow flex-shrink-0">
            <i className="ri-car-line text-brand-blue-dark text-3xl" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-heading text-white text-2xl font-black mb-2">We Come to You — No Office Visit Needed</h3>
            <p className="font-body text-white/70 text-sm leading-relaxed max-w-xl">
              Creative Institute is 100% mobile and in-home. We travel to your home, school, church, or community space across 20+ cities in North Carolina. Just reach out and we&apos;ll confirm your location.
            </p>
          </div>
          <div className="flex flex-col gap-3 flex-shrink-0">
            <a href="tel:9102424567" className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-all">
              <i className="ri-phone-line text-sm" /> Call 910-242-4567
            </a>
            <span className="font-body text-white/50 text-xs text-center">Mon – Sat · 9am – 9pm</span>
          </div>
        </div>

        {/* City grid */}
        <div className="rounded-3xl bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7]/40 border border-brand-yellow/20 p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h4 className="font-heading text-brand-blue-dark text-lg font-black mb-1">Cities We Serve</h4>
              <p className="font-body text-gray-500 text-xs">We travel to all of these locations and more across NC</p>
            </div>
            <Link to="/cities-we-serve" className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-brand-blue-dark text-brand-blue-dark font-body font-semibold text-xs cursor-pointer hover:bg-brand-blue-dark hover:text-white transition-all">
              View Full Service Area <i className="ri-arrow-right-line text-xs" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {cities.map((city) => (
              <span key={city} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-brand-yellow/30 font-body text-brand-blue-dark text-xs font-semibold">
                <i className="ri-map-pin-2-fill text-[#D97706] text-xs" /> {city}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-blue-dark font-body text-white text-xs font-semibold">
              <i className="ri-add-line text-xs" /> And More
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function ScheduleSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  return (
    <section className="py-24 bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6] relative overflow-hidden">
      <div className="absolute inset-0">
        <img src="https://public.readdy.ai/ai/img_res/602a7034ab5c8ca90b0d6d174e5f5252.jpg" alt="" className="w-full h-full object-cover object-top opacity-10" />
      </div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div ref={ref} className="relative z-10 max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} flex flex-col lg:flex-row items-center gap-14`}>
          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/40 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-5">Free &amp; No Commitment</span>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
              Schedule a Free<br /><span className="text-brand-yellow">Consultation</span>
            </h2>
            <p className="font-body text-white/75 text-base leading-relaxed max-w-md mx-auto lg:mx-0 mb-6">
              Ready to take the next step? Use our AI assistant to pick a date and time that works for you — and we&apos;ll connect to talk through your goals, the right program, or any questions you have.
            </p>
            <ul className="flex flex-col gap-3 max-w-sm mx-auto lg:mx-0 mb-8">
              {['Choose a date and time that works for you', 'Talk through your goals and creative vision', 'Get matched with the right program or service', 'No commitment required — just a conversation'].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-yellow/20 flex-shrink-0"><i className="ri-check-line text-brand-yellow text-xs" /></div>
                  <span className="font-body text-white/80 text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <button onClick={() => { const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null; if (btn) btn.click(); }} className="whitespace-nowrap inline-flex items-center gap-3 px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-base cursor-pointer hover:bg-brand-yellow-light transition-all">
              <i className="ri-calendar-check-line text-lg" /> Schedule a Free Consultation
            </button>
          </div>
          <div className={`scroll-hidden delay-200 ${isVisible ? 'scroll-visible' : ''} flex-shrink-0 w-full lg:w-[360px]`}>
            <div className="rounded-3xl bg-white/10 border border-white/20 backdrop-blur-sm p-8">
              <div className="flex items-center gap-4 mb-7">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-brand-yellow text-brand-blue-dark flex-shrink-0"><i className="ri-user-voice-line text-2xl" /></div>
                <div>
                  <p className="font-heading text-white text-base font-bold">Creative Institute</p>
                  <p className="font-body text-white/60 text-xs mt-0.5">Free Consultation Call</p>
                </div>
              </div>
              <div className="flex flex-col gap-4 mb-7">
                {[{ icon: 'ri-time-line', label: 'Duration', value: '20–30 minutes' }, { icon: 'ri-phone-line', label: 'Format', value: 'Phone or Video Call' }, { icon: 'ri-map-pin-line', label: 'Location', value: 'Multiple Cities Across North Carolina (Mobile or Remote)' }, { icon: 'ri-price-tag-3-line', label: 'Cost', value: 'Completely Free' }].map((detail) => (
                  <div key={detail.label} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 text-brand-yellow flex-shrink-0"><i className={`${detail.icon} text-sm`} /></div>
                    <div>
                      <p className="font-body text-white/50 text-xs">{detail.label}</p>
                      <p className="font-body text-white text-sm font-semibold">{detail.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-5 border-t border-white/15">
                <p className="font-heading italic text-white/70 text-sm leading-relaxed">&ldquo;We want to understand your goals and make sure Creative Institute is the right fit for you.&rdquo;</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LeaveReviewSection() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: formRef, isVisible: formVis } = useScrollAnimation({ threshold: 0.1 });
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const ratingLabels: Record<number, string> = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Great', 5: 'Outstanding!' };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (rating === 0) return;
    const form = e.currentTarget;
    const review = (form.elements.namedItem('review') as HTMLTextAreaElement)?.value || '';
    if (review.length > 500) return;
    setStatus('submitting');
    const formData = new URLSearchParams();
    formData.append('rating', String(rating));
    const els = form.elements;
    for (let i = 0; i < els.length; i++) {
      const el = els[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value && el.type !== 'submit') formData.append(el.name, el.value);
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d72q3u4ij602egd95ne0', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: formData.toString() });
      if (res.ok) { setStatus('success'); form.reset(); setRating(0); setCharCount(0); } else setStatus('error');
    } catch { setStatus('error'); }
  };

  return (
    <section className="py-28 bg-gradient-to-br from-[#FFFBF2] via-white to-[#F5F3FF] relative overflow-hidden">
      <div className="absolute top-0 right-1/4 w-80 h-80 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-brand-purple/8 rounded-full blur-3xl pointer-events-none" />
      <div className="relative z-10 max-w-screen-xl mx-auto px-8">
        <div ref={titleRef} className="text-center mb-14">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/20 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-yellow/30 mb-4`}>Share Your Experience</span>
          <h2 className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-heading italic text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4`}>Leave a Review</h2>
          <p className={`scroll-hidden delay-300 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base max-w-xl mx-auto leading-relaxed`}>Your words inspire other families to take the first step. We&apos;d be honored to hear about your experience.</p>
        </div>
        <div ref={formRef} className={`scroll-hidden ${formVis ? 'scroll-visible' : ''} max-w-2xl mx-auto`}>
          {status === 'success' ? (
            <div className="flex flex-col items-center justify-center text-center gap-5 py-16 px-10 rounded-3xl bg-white border border-brand-yellow/20">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-brand-yellow/20"><i className="ri-heart-fill text-4xl text-[#D97706]" /></div>
              <h3 className="font-heading italic text-brand-blue-dark text-2xl font-black">Thank You So Much!</h3>
              <p className="font-body text-gray-500 text-sm leading-relaxed max-w-sm">Your review has been received and truly means the world to us.</p>
              <div className="flex gap-1">{[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-brand-yellow text-xl" />)}</div>
              <button onClick={() => setStatus('idle')} className="whitespace-nowrap mt-2 px-7 py-3 rounded-full border-2 border-brand-yellow text-brand-yellow-dark font-body font-semibold text-sm cursor-pointer hover:bg-brand-yellow hover:text-brand-blue-dark transition-colors">Leave Another Review</button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-gray-100 p-10 flex flex-col gap-6">
              <div>
                <p className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">Your Rating *</p>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button key={star} type="button" onClick={() => setRating(star)} onMouseEnter={() => setHovered(star)} onMouseLeave={() => setHovered(0)} className="w-10 h-10 flex items-center justify-center cursor-pointer transition-transform duration-150 hover:scale-125 focus:outline-none" aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}>
                        <i className={`text-2xl transition-colors duration-150 ${star <= (hovered || rating) ? 'ri-star-fill text-brand-yellow' : 'ri-star-line text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                  {(hovered || rating) > 0 && <span className="font-body text-sm font-semibold text-brand-yellow-dark">{ratingLabels[hovered || rating]}</span>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2 block">Your Name *</label>
                  <input name="name" type="text" required placeholder="e.g. Sarah M." className="w-full text-sm font-body border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:outline-none focus:border-brand-yellow transition-colors placeholder:text-gray-300" />
                </div>
                <div>
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2 block">Role</label>
                  <select name="role" className="w-full text-sm font-body border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:outline-none focus:border-brand-yellow transition-colors text-gray-600">
                    <option value="">Select your role</option>
                    <option value="Parent / Guardian">Parent / Guardian</option>
                    <option value="Student">Student</option>
                    <option value="Community Member">Community Member</option>
                    <option value="Partner Organization">Partner Organization</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2 block">Email</label>
                  <input name="email" type="email" placeholder="your@email.com" className="w-full text-sm font-body border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:outline-none focus:border-brand-yellow transition-colors placeholder:text-gray-300" />
                </div>
                <div>
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2 block">Program / Service</label>
                  <select name="program" className="w-full text-sm font-body border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:outline-none focus:border-brand-yellow transition-colors text-gray-600">
                    <option value="">Select a program</option>
                    <option value="In-Home Creative Program">In-Home Creative Program</option>
                    <option value="On-Site Program">On-Site Program</option>
                    <option value="Off-Site Service">Off-Site Service</option>
                    <option value="Recording Studio">Recording Studio</option>
                    <option value="Neurodivergent Development">Neurodivergent Development</option>
                    <option value="General">General</option>
                  </select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest">Your Review *</label>
                  <span className={`font-body text-xs ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>{charCount}/500</span>
                </div>
                <textarea name="review" required maxLength={500} rows={4} onChange={(e) => setCharCount(e.target.value.length)} placeholder="Tell us about your experience..." className="w-full text-sm font-body border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:outline-none focus:border-brand-yellow transition-colors resize-none placeholder:text-gray-300" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer group">
                <input type="checkbox" name="consent_public" value="yes" className="sr-only" />
                <div onClick={(e) => { const el = e.currentTarget.previousElementSibling as HTMLInputElement | null; if (el) el.click(); e.currentTarget.classList.toggle('bg-gradient-to-br'); e.currentTarget.classList.toggle('from-brand-yellow'); e.currentTarget.classList.toggle('to-[#D97706]'); e.currentTarget.classList.toggle('border-transparent'); }} className="w-5 h-5 rounded-md border-2 border-gray-300 flex items-center justify-center flex-shrink-0 mt-0.5 cursor-pointer transition-all group-hover:border-brand-yellow">
                  <i className="ri-check-line text-white text-xs hidden" />
                </div>
                <span className="font-body text-gray-500 text-xs leading-relaxed cursor-pointer group-hover:text-gray-700 transition-colors">
                  <strong className="text-gray-700 font-semibold">I give Creative Institute permission</strong> to feature my review publicly. <span className="text-gray-400">(Unchecked = private only)</span>
                </span>
              </label>
              {status === 'error' && <p className="font-body text-red-500 text-sm text-center">Something went wrong. Please try again.</p>}
              <button type="submit" disabled={status === 'submitting' || rating === 0 || charCount > 500} className="whitespace-nowrap w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-[#D97706] to-[#7C3AED] text-white font-body font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
                {status === 'submitting' ? <><i className="ri-loader-4-line animate-spin" /> Submitting...</> : <><i className="ri-star-fill" /> Submit My Review</>}
              </button>
              {rating === 0 && <p className="font-body text-gray-400 text-xs text-center -mt-2">Please select a star rating before submitting.</p>}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const contactJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Creative Institute LLC',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
  description: 'Contact Creative Institute LLC for mobile music lessons, in-home creative programs, and arts education across North Carolina.',
  mainEntity: {
    '@type': 'EducationalOrganization',
    name: 'Creative Institute LLC',
    telephone: '910-242-4567',
    email: 'info@creativeinstitutellc.com',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '21:00',
      },
    ],
  },
};

export default function ContactPage() {
  return (
    <main>
      <SEOHead
        title="Contact Us | Creative Institute LLC — Mobile Music & Arts NC"
        description="Reach out to Creative Institute LLC. We come to you — mobile music lessons, in-home creative programs, and arts education across Fayetteville, Raleigh, Durham, and 20+ NC cities."
        keywords="contact Creative Institute, mobile music lessons NC, in-home arts education, Fayetteville music lessons, North Carolina creative programs"
        canonical="/contact"
        jsonLd={contactJsonLd}
      />
      <PageHero />
      <ContactSection />
      <MapSection />
      <ScheduleSection />
      <LeaveReviewSection />
    </main>
  );
}
